import { InfoWindow } from '@react-google-maps/api'
import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { usePermissions } from '../../../hooks'
import DateTime from '../../DateTime'
import { useTargetUserName } from '../../../hooks/useUserDisplayName'
import { useMapSettings } from '.'
import DeleteConfirmationModal from '../../DeleteConfirmationModal'
import ColorSelectModal from '../../ColorSelectModal'
import { parseARGB } from '../../../hooks'

const TrackInfoWindow = ({
  track,
  anchor,
  onClose,
  tracks,
  currentUserId,
  onDeleteTrack,
  liveTrackData // Add this prop for live updates
}) => {
  const deletePermission = usePermissions('track', 'delete')
  const [{ showLastName }] = useMapSettings()
  const [currentTrack, setCurrentTrack] = useState(track)
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingColor, setIsEditingColor] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [trackName, setTrackName] = useState(currentTrack?.name || '')
  const [selectedColor, setSelectedColor] = useState(currentTrack?.color_code || '')
  const [isSavingName, setIsSavingName] = useState(false)
  const [isSavingColor, setIsSavingColor] = useState(false)
  const [availableColors, setAvailableColors] = useState([])
  const [isLoadingColors, setIsLoadingColors] = useState(false)
  // Add these state variables after your existing states (around line 20)
  const [showColorModal, setShowColorModal] = useState(false)
  const { userName, loading } = useTargetUserName(currentTrack?.user_id, showLastName);

  // Update track data when live data arrives or prop changes
  useEffect(() => {
    if (liveTrackData && liveTrackData.id === track.id) {
      setCurrentTrack((prevTrack) => ({
        ...prevTrack,
        ...liveTrackData,
        points: liveTrackData.points || prevTrack.points
      }))
    }
  }, [liveTrackData, track.id])

  // Also update when the main track prop changes
  useEffect(() => {
    setCurrentTrack(track)
    setTrackName(track?.name || '')
    setSelectedColor(track?.color_code || '')
  }, [track])

  if (!currentTrack || !anchor) return null

  const isCreator = currentTrack.user_id === currentUserId
  const startPoint = currentTrack.points?.[0]
  const endPoint = currentTrack.points?.[currentTrack.points.length - 1]
  const latestPoint = currentTrack.points?.[currentTrack.points.length - 1]

  const handleDelete = async () => {
    try {
      await tracks.deleteTrack(currentTrack.id)
      onDeleteTrack(currentTrack.id)
      onClose()
      toast.success('Track deleted successfully')
    } catch (error) {
      console.error('Failed to delete track:', error)
      toast.error('Failed to delete track')
    }
  }

  const handleSaveTrackName = async () => {
    if (!trackName.trim()) {
      toast.error('Track name cannot be empty')
      return
    }

    try {
      setIsSavingName(true)

      // use existing global service
      await tracks.updateTrackName(currentTrack.id, trackName)

      setCurrentTrack((prev) => ({
        ...prev,
        name: trackName
      }))

      setIsEditingName(false)
      toast.success('Track name updated successfully')
    } catch (error) {
      toast.error('Failed to update track name')
    } finally {
      setIsSavingName(false)
    }
  }

  const handleEditColor = async () => {
    try {
      setIsLoadingColors(true)
      const response = await tracks.getTrackColors()
      setAvailableColors(response.data || [])
      setShowColorModal(true) // Open modal instead of inline editing
      setSelectedColor(currentTrack?.color_code || '') // Reset selected color
    } catch (error) {
      console.error('Failed to load colors:', error)
      toast.error('Failed to load color options')
    } finally {
      setIsLoadingColors(false)
    }
  }

  const handleSaveColor = async () => {
    if (!selectedColor || selectedColor === currentTrack?.color_code) {
      return
    }

    try {
      setIsSavingColor(true)
      await tracks.updateTrackColor(currentTrack.id, selectedColor)

      setCurrentTrack(prev => ({
        ...prev,
        color_code: selectedColor
      }))

      toast.success('Track color updated successfully')
    } catch (error) {
      console.error('Failed to update track color:', error)
      toast.error('Failed to update track color')
    } finally {
      setIsSavingColor(false)
    }
  }

  const handleExport = async () => {
    try {
      const response = await tracks.exportKml(currentTrack.id) // API returns blob/text

      // Create a file blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.google-earth.kml+xml'
      })

      const url = window.URL.createObjectURL(blob)

      // Create a temporary link to trigger download
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `track_${currentTrack.id}.kml`)
      document.body.appendChild(link)
      link.click()

      // Cleanup
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('KML exported successfully')
    } catch (error) {
      console.error('Failed to export KML:', error)
      toast.error('Failed to export KML')
    }
  }

  const formatDuration = (seconds) => {
    const totalSeconds = Math.floor(seconds) // remove decimals if present

    const hrs = Math.floor(totalSeconds / 3600)
    const mins = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`
    } else if (mins > 0) {
      return `${mins}m ${secs}s`
    } else {
      return `${secs}s`
    }
  }



  return (
    <>
      <InfoWindow
        key={isEditingName || showColorModal ? 'edit' : 'view'}
        anchor={anchor}
        onCloseClick={onClose}
        options={{
          maxWidth: 400,
          pixelOffset: new window.google.maps.Size(0, -40),
          autoPanPadding: { top: 180, bottom: 50, left: 50, right: 50 }
        }}>
        <>
          <div className='text-lg font-semibold text-gray-900 pb-2 border-b'>
            Track Info
          </div>
          <div className='w-full max-w-[400px] py-4 px-2 bg-white rounded-lg shadow-lg'>
            {/* Header with Live Indicator */}
            <div className='flex items-center justify-between mb-3'>
              <div className='flex items-center gap-2'>
                <h3 className='text-lg !font-semibold !text-black'>
                  Track #{currentTrack.id}
                </h3>
                {currentTrack.status === 'active' && (
                  <span className='px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full animate-pulse'>
                    LIVE
                  </span>
                )}
              </div>
              <div className='flex items-center gap-2'>
                <button
                  onClick={handleExport}
                  className='p-2 rounded bg-blue-100 text-blue-600 hover:bg-blue-200 cursor-pointer'
                  title='Export KML'>
                  <i className='fa-solid fa-file-export'></i>
                </button>

                {(isCreator || deletePermission?.granted) && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className='text-red-500 hover:text-red-700 cursor-pointer'
                    title='Delete track'>
                    <i className='fa-regular fa-trash-can'></i>
                  </button>
                )}
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: currentTrack.color_code || '#3366ff',
                    border: '2px solid white'
                  }}
                />
              </div>
            </div>

            {/* Content */}
            <div className='space-y-2 mb-4'>
              {/* Track Name Row */}
              <div className='flex items-center text-sm gap-2'>
                <span className='font-medium text-gray-700'>Track Name:</span>

                {!isEditingName || !isCreator ? (
                  <>
                    <span className='ml-1 capitalize text-gray-800'>
                      {currentTrack?.name || 'NA'}
                    </span>

                    {isCreator && (
                      <button
                        onClick={() => setIsEditingName(true)}
                        className='text-blue-600 hover:text-blue-800 text-xs cursor-pointer'>
                        {currentTrack?.name ? 'Edit' : 'Add'}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type='text'
                      value={trackName}
                      onChange={(e) => setTrackName(e.target.value)}
                      className='border rounded px-2 py-1 text-sm w-40'
                      placeholder='Enter track name'
                    />

                    <button
                      onClick={handleSaveTrackName}
                      className='text-green-600 hover:text-green-800 text-xs cursor-pointer'>
                      Save
                    </button>

                    <button
                      onClick={() => {
                        setIsEditingName(false)
                        setTrackName(currentTrack?.name || '')
                      }}
                      className='text-gray-500 hover:text-gray-700 text-xs cursor-pointer'>
                      Cancel
                    </button>
                  </>
                )}
              </div>

              {/* Track Color Row - Simplified */}
              <div className='flex items-center text-sm gap-2'>
                <span className='font-medium text-gray-700'>Track Color:</span>
                <>
                  <div
                    className='ml-1 w-5 h-5 rounded-full border border-gray-300'
                    style={{ backgroundColor: parseARGB(currentTrack.color_code).color || (currentTrack.color_code || '#3366ff') }}
                  />

                  {isCreator && (
                    <button
                      onClick={handleEditColor}
                      className='text-blue-600 hover:text-blue-800 text-xs cursor-pointer'>
                      Change
                    </button>
                  )}
                </>
              </div>

              <div className='flex items-center text-sm'>
                <span className='font-medium text-gray-700'>Status:</span>
                <span
                  className={`ml-1 capitalize ${currentTrack.status === 'ended'
                    ? 'text-red-600'
                    : currentTrack.status === 'in_progress'
                      ? 'text-green-600'
                      : 'text-gray-600'
                    }`}>
                  {currentTrack.status || 'active'}
                  {currentTrack.status === 'in_progress' && ' 🔴'}
                </span>
              </div>

              <div className='flex items-center text-sm'>
                <span className='font-medium text-gray-700'>Duration:</span>
                <span className='ml-1 text-gray-600'>
                  {currentTrack.total_duration
                    ? formatDuration(currentTrack.total_duration)
                    : currentTrack.status === 'in_progress'
                      ? 'Counting...'
                      : 'N/A'}
                </span>
              </div>

              <div className='flex items-center text-sm'>
                <span className='font-medium text-gray-700'>Distance:</span>
                <span className='ml-1 text-gray-600'>
                  {currentTrack.total_distance
                    ? `${(currentTrack.total_distance / 1000).toFixed(2)} km`
                    : currentTrack.status === 'in_progress'
                      ? 'Calculating...'
                      : 'N/A'}
                </span>
              </div>

              <div className='flex items-center text-sm'>
                <span className='font-medium text-gray-700'>Points:</span>
                <span className='ml-1 text-gray-600'>
                  {currentTrack.points?.length || 0}
                  {currentTrack.status === 'in_progress' && ' +'}
                </span>
              </div>

              <div className='flex items-center text-sm'>
                <span className='font-medium text-gray-700'>Started:</span>
                <span className='ml-1 text-gray-600'>
                  <DateTime value={currentTrack.started_at} />
                </span>
              </div>

              {currentTrack.ended_at && (
                <div className='flex items-center text-sm'>
                  <span className='font-medium text-gray-700'>Ended:</span>
                  <span className='ml-1 text-gray-600'>
                    <DateTime value={currentTrack.ended_at} />
                  </span>
                </div>
              )}

              {currentTrack.user_id && (
                <div className='flex items-center text-sm'>
                  <span className='font-medium text-gray-700'>Created by:</span>
                  <span className='ml-1 text-gray-600'>
                    {userName}
                  </span>
                </div>
              )}

            </div>

            {/* Latest Point Information */}
            {latestPoint && (
              <div className='mt-3 pt-3 border-t border-gray-100'>
                <h4 className='text-lg !font-semibold !text-black mb-2'>
                  {currentTrack.status === 'in_progress'
                    ? 'Latest Position'
                    : 'Final Position'}
                </h4>
                <div className='flex items-center text-sm mb-1'>
                  <span
                    className={`mr-2 ${currentTrack.status === 'in_progress'
                      ? 'text-green-600 animate-pulse'
                      : 'text-gray-600'
                      }`}>
                    ●
                  </span>
                  <span>
                    {latestPoint.latitude.toFixed(6)},{' '}
                    {latestPoint.longitude.toFixed(6)}
                  </span>
                </div>
                {latestPoint.timestamp && (
                  <div className='text-xs text-gray-500 mt-1'>
                    Updated: <DateTime value={latestPoint.timestamp} />
                  </div>
                )}
              </div>
            )}

            {/* Start and End Points */}
            {(startPoint || endPoint) && startPoint !== endPoint && (
              <div className='mt-3 pt-3 border-t border-gray-100'>
                <h4 className='text-lg !font-semibold !text-black mb-2'>
                  Path Summary
                </h4>
                {startPoint && (
                  <div className='flex items-center text-sm mb-1'>
                    <span className='text-green-600 mr-2'>●</span>
                    <span className='text-xs'>
                      Start: {startPoint.latitude.toFixed(6)},{' '}
                      {startPoint.longitude.toFixed(6)}
                    </span>
                  </div>
                )}
                {endPoint && startPoint !== endPoint && (
                  <div className='flex items-center text-sm'>
                    <span className='text-red-600 mr-2'>●</span>
                    <span className='text-xs'>
                      End: {endPoint.latitude.toFixed(6)},{' '}
                      {endPoint.longitude.toFixed(6)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      </InfoWindow>
      <ColorSelectModal
        open={showColorModal}
        onClose={() => setShowColorModal(false)}
        availableColors={availableColors}
        selectedColor={selectedColor}
        setSelectedColor={setSelectedColor}
        isSaving={isSavingColor}
        onSave={async () => {
          await handleSaveColor();
          setShowColorModal(false);
        }}
      />

      <DeleteConfirmationModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          await handleDelete()
          setShowDeleteConfirm(false)
        }}
        title="Delete Track"
        message="Are you sure you want to delete this track?"
      />
    </>
  )
}

export default TrackInfoWindow